import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import produce from 'immer';
import React, {
  type CSSProperties,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Input} from 'semantic-ui-react';

import * as ViewHooks from '../../state/views/hooks';
import type * as PanelTypes from '../../state/views/panel/types';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import type {SectionNormalized} from '../../state/views/section/types';
import type {LayedOutPanel} from '../../util/panelTypes';
import {ImportPanelList} from './ImportPanelList';
import * as S from './ImportPanelMenu.styles';
import {usePanelsForProject, useTeamsAndProjects} from './importPanelMenuHooks';

type EntitiesMenuItem =
  | {type: 'heading'; entityName: string}
  | {type: 'item'; entityName: string; projectName: string};

type PanelsMenuItem =
  | {type: 'heading'; sectionName: string}
  | {type: 'panel'; panelName: string; panel: LayedOutPanel};

type ImportPanelMenuProps = {
  entityName: string;
  projectName: string;
  sectionPart: SectionNormalized;
};

function ImportPanelMenuComponent(props: ImportPanelMenuProps) {
  const [projectName, setProjectName] = useState(props.projectName);
  const [entityName, setEntityName] = useState(props.entityName);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [panelSearchQuery, setPanelSearchQuery] = useState('');
  const [selectedPanelRefs, setSelectedPanelRefs] = useState<
    Record<string, PanelTypes.Ref>
  >({});

  const addPanelByConfig = ViewHooks.useViewAction(
    props.sectionPart.panelBankSectionConfigRef,
    PanelBankSectionConfigActions.addPanelByConfig
  );

  const deletePanel = ViewHooks.useViewAction(
    props.sectionPart.panelBankSectionConfigRef,
    PanelBankSectionConfigActions.deletePanel
  );

  const teamsAndProjects = useTeamsAndProjects({
    selectedEntityName: props.entityName,
    projectSearchQuery,
  });

  const viewSections = usePanelsForProject({
    entityName,
    projectName,
  });

  const filteredViewSections = useMemo(() => {
    const search = panelSearchQuery.toLocaleLowerCase();
    return viewSections
      .map(section => {
        return {
          ...section,
          panels: section.panels.filter(panel => {
            return panel.name.toLocaleLowerCase().includes(search);
          }),
        };
      })
      .filter(section => Boolean(section.panels.length));
  }, [panelSearchQuery, viewSections]);

  const onSelectPanel = useCallback(
    (panel: LayedOutPanel) => {
      const alreadyAddedPanelRef = selectedPanelRefs[panel.__id__];
      if (alreadyAddedPanelRef != null) {
        deletePanel(alreadyAddedPanelRef);
        const newSelectedPanelRefs = produce(selectedPanelRefs, draft => {
          delete draft[panel.__id__];
        });
        setSelectedPanelRefs(newSelectedPanelRefs);
      } else {
        window.analytics?.track(`Panel Imported To Report`, {
          location: `import panel menu`,
        });
        const addNewPanelRefCallback = (
          originalPanel: LayedOutPanel,
          newPanelRef: PanelTypes.Ref
        ) => {
          const newSelectedPanelRefs = produce(selectedPanelRefs, draft => {
            draft[originalPanel.__id__] = newPanelRef;
          });
          setSelectedPanelRefs(newSelectedPanelRefs);
        };
        addPanelByConfig(panel, false, addNewPanelRefCallback);
      }
    },
    [addPanelByConfig, deletePanel, selectedPanelRefs]
  );

  const entitiesMenuList: readonly EntitiesMenuItem[] = useMemo(() => {
    // entity/project that the report belongs to is selected by default,
    // so make sure it's displayed at the top of the list
    const matchingTeam = teamsAndProjects.find(
      team => team.name === entityName
    );
    const sortedTeams = matchingTeam
      ? [
          matchingTeam,
          ...teamsAndProjects.filter(team => team.name !== entityName),
        ]
      : teamsAndProjects;

    return sortedTeams.flatMap(team => {
      const isMatchingTeam = team.name === entityName;
      const matchingProject = isMatchingTeam
        ? team.projects.find(project => project.name === projectName)
        : undefined;
      const sortedProjects = matchingProject
        ? [
            matchingProject,
            ...team.projects.filter(project => project.name !== projectName),
          ]
        : team.projects;
      return [
        {type: 'heading', entityName: team.name},
        ...sortedProjects.map(
          project =>
            ({
              type: 'item',
              entityName: team.name,
              projectName: project.name,
            } as const)
        ),
      ];
    });
  }, [entityName, projectName, teamsAndProjects]);

  const renderEntityListItem = useCallback(
    (i: EntitiesMenuItem, style: CSSProperties) => {
      if (i.type === 'heading') {
        return <S.ListHeader style={style}>{i.entityName}</S.ListHeader>;
      }
      return (
        <S.ProjectItem
          style={style}
          selected={i.projectName === projectName}
          onClick={() => {
            setProjectName(i.projectName);
            setEntityName(i.entityName);
          }}>
          {i.projectName}
        </S.ProjectItem>
      );
    },
    [projectName]
  );

  const panelsMenuList: readonly PanelsMenuItem[] = useMemo(
    () =>
      filteredViewSections.flatMap(sectionPanelItem => {
        const {sectionName, panels} = sectionPanelItem;

        return [
          {type: 'heading', sectionName},
          ...panels.map(
            panel =>
              ({
                type: 'panel',
                panelName: panel.name,
                panel: panel.panel,
              } as const)
          ),
        ];
      }),
    [filteredViewSections]
  );

  const renderPanelListItem = useCallback(
    (i: PanelsMenuItem, style: CSSProperties) => {
      if (i.type === 'heading') {
        return <S.ListHeader style={style}>{i.sectionName}</S.ListHeader>;
      }
      const selected = Boolean(selectedPanelRefs[i.panel.__id__]);
      return (
        <S.PanelItem
          style={style}
          selected={selected}
          onClick={() => onSelectPanel(i.panel)}>
          <S.TextWrapper>{i.panelName}</S.TextWrapper>
          {selected ? (
            <S.CheckIconWrapper>
              <S.CheckIcon name="check" />
            </S.CheckIconWrapper>
          ) : null}
        </S.PanelItem>
      );
    },
    [onSelectPanel, selectedPanelRefs]
  );

  return (
    <S.Wrapper>
      <S.ProjectSection>
        <S.SearchWrapper>
          <Input
            value={projectSearchQuery}
            transparent
            onChange={(e, {value}) => setProjectSearchQuery(value)}
            icon={<LegacyWBIcon name="search" size="large" />}
            iconPosition="left"
            placeholder="Search projects"
          />
        </S.SearchWrapper>
        <ImportPanelList
          data-test="import-panels-projects-list"
          items={entitiesMenuList}
          itemSize={30}
          height={380}
          width={215}
          renderItem={renderEntityListItem}
        />
      </S.ProjectSection>
      <S.HorizontalDivider />
      <S.PanelSection>
        <S.SearchWrapper>
          <Input
            value={panelSearchQuery}
            transparent
            onChange={(e, {value}) => setPanelSearchQuery(value)}
            icon={<LegacyWBIcon name="search" size="large" />}
            iconPosition="left"
            placeholder="Search panels"
          />
        </S.SearchWrapper>
        <ImportPanelList
          data-test="import-panel-panels-list"
          items={panelsMenuList}
          itemSize={30}
          height={380}
          width={320}
          renderItem={renderPanelListItem}
        />
      </S.PanelSection>
    </S.Wrapper>
  );
}

export default memo(ImportPanelMenuComponent);
